import { streamChessComImport, type ChessComImportFilters, type ImportProgress } from "./games.api";
import { useGamesStore } from "./games.store";
import { useAnalysisStore } from "../analysis/analysis.store";
import { trackProductEvent } from "@/shared/analytics/ga4";

class ImportRunner {
  private controller: AbortController | null = null;

  get running(): boolean {
    return this.controller != null;
  }

  /** `quiet` skips the shared progress UI, for the silent catch-up import. */
  async start(
    limit: number | "all",
    filters: ChessComImportFilters = {},
    { quiet = false }: { quiet?: boolean } = {},
  ): Promise<ImportProgress> {
    if (this.controller) throw new Error("An import is already running");
    if (useAnalysisStore.getState().isRunning) throw new Error("Nikola is already analyzing your games");
    const controller = new AbortController();
    const ownerUserId = useGamesStore.getState().ownerUserId;
    this.controller = controller;
    if (!quiet)
      useGamesStore
        .getState()
        .setImportProgress({ imported: 0, target: limit === "all" ? null : limit, phase: "finding" });
    try {
      const result = await streamChessComImport(
        limit,
        controller.signal,
        (progress) => {
          if (!quiet && useGamesStore.getState().ownerUserId === ownerUserId)
            useGamesStore.getState().setImportProgress(progress);
        },
        filters,
      );
      if (useGamesStore.getState().ownerUserId === ownerUserId && result.phase === "complete" && result.imported > 0)
        trackProductEvent("import_completed");
      return result;
    } finally {
      if (this.controller === controller) this.controller = null;
      if (!quiet && useGamesStore.getState().ownerUserId === ownerUserId)
        useGamesStore.getState().setImportProgress(null);
    }
  }

  stop(): void {
    this.controller?.abort();
    this.controller = null;
  }
}

export const importRunner = new ImportRunner();
